import React, { useState, useEffect, useRef } from 'react';
import { X, Send, RotateCcw, Loader2, Bot, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ChatbotPopup = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Load chat history from localStorage
  useEffect(() => {
    const savedMessages = localStorage.getItem('thinkverse_chat_history');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    } else {
      // Welcome message
      setMessages([
        {
          role: 'assistant',
          content: "Hello! I'm your ThinkVerse Assistant. I'm ready to help you explore our services, mission, and technologies. How can I assist you?",
          timestamp: new Date().toISOString()
        }
      ]);
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('thinkverse_chat_history', JSON.stringify(messages));
    }
  }, [messages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chatbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          conversation_history: messages.slice(-6) // Last 3 exchanges
        })
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('API key is not configured');
        }
        throw new Error('Failed to get response from chatbot');
      }

      const data = await response.json();
      
      const assistantMessage = {
        role: 'assistant',
        content: data.message,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      const isApiKeyError = error.message.includes('API key is not configured');
      const errorContent = isApiKeyError
        ? '⚠️ **Intelligence Offline**\nThe official ThinkVerse chatbot requires a Gemini API Key to function. Please ensure this is configured in the backend environment.'
        : 'I apologize, but I encountered an error processing your request. Please try again.';

      const assistantMessage = {
        role: 'assistant',
        content: errorContent,
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleResetConversation = () => {
    const welcomeMessage = {
      role: 'assistant',
      content: 'Welcome to ThinkVerse.AI! I am here to provide factual information about ThinkVerse.AI based on our official datasource. How may I assist you today?',
      timestamp: new Date().toISOString()
    };
    setMessages([welcomeMessage]);
    localStorage.setItem('thinkverse_chat_history', JSON.stringify([welcomeMessage]));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-50 w-full sm:w-[420px] h-full sm:h-[650px] bg-white border-0 sm:border-2 border-blue-200 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-sky-500 p-5 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md">
            <img 
              src="https://customer-assets.emergentagent.com/job_intelligent-home-1/artifacts/w793r8mv_ThinkVerse.AI%20%282%29.png"
              alt="ThinkVerse.AI"
              className="w-10 h-10 object-contain"
            />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">ThinkVerse.AI</h3>
            <p className="text-white/90 text-sm">Talk with TV!</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleResetConversation}
            className="text-white/90 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
            title="Reset conversation"
          >
            <RotateCcw size={18} />
          </button>
          <button
            onClick={onClose}
            className="text-white/90 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
            title="Close chat"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-white">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role === 'assistant' && (
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-blue-200">
                <img 
                  src="https://customer-assets.emergentagent.com/job_intelligent-home-1/artifacts/w793r8mv_ThinkVerse.AI%20%282%29.png"
                  alt="TV"
                  className="w-5 h-5 object-contain"
                />
              </div>
            )}
            <div
              className={`max-w-[75%] p-3 rounded-2xl ${
                message.role === 'user'
                  ? 'bg-gradient-to-r from-blue-500 to-sky-500 text-white shadow-md'
                  : 'bg-white text-gray-800 border border-gray-200 shadow-sm'
              }`}
            >
              {message.role === 'user' ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
              ) : (
                <div className="text-sm leading-relaxed prose prose-slate prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Custom styling for markdown elements
                      p: ({node, ...props}) => <p className="mb-2 last:mb-0 text-gray-700" {...props} />,
                      ul: ({node, ...props}) => <ul className="list-disc ml-4 mb-2 space-y-1" {...props} />,
                      ol: ({node, ...props}) => <ol className="list-decimal ml-4 mb-2 space-y-1" {...props} />,
                      li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
                      strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                      em: ({node, ...props}) => <em className="italic text-gray-700" {...props} />,
                      code: ({node, inline, ...props}) => 
                        inline ? (
                          <code className="bg-blue-50 px-1.5 py-0.5 rounded text-blue-600 font-mono" {...props} />
                        ) : (
                          <code className="block bg-gray-100 p-2 rounded my-2 text-sm font-mono text-gray-800" {...props} />
                        ),
                      a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-700 underline" {...props} />,
                      h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-2 text-gray-900" {...props} />,
                      h2: ({node, ...props}) => <h2 className="text-base font-bold mb-2 text-gray-900" {...props} />,
                      h3: ({node, ...props}) => <h3 className="text-sm font-bold mb-2 text-gray-900" {...props} />,
                      blockquote: ({node, ...props}) => <blockquote className="border-l-2 border-blue-500 pl-3 italic text-gray-600 my-2" {...props} />,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              )}
            </div>
            {message.role === 'user' && (
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-sky-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                <User size={18} className="text-white" />
              </div>
            )}
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex gap-3 justify-start">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-blue-200">
              <img 
                src="https://customer-assets.emergentagent.com/job_intelligent-home-1/artifacts/w793r8mv_ThinkVerse.AI%20%282%29.png"
                alt="TV"
                className="w-5 h-5 object-contain"
              />
            </div>
            <div className="bg-white border border-gray-200 shadow-sm p-3 rounded-2xl">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-gray-200">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about ThinkVerse.AI..."
            disabled={isLoading}
            className="flex-1 bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSendMessage}
            disabled={isLoading || !inputValue.trim()}
            className="bg-gradient-to-r from-blue-500 to-sky-500 hover:from-sky-500 hover:to-blue-500 text-white p-3 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-md hover:shadow-lg"
          >
            {isLoading ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Responses based on official ThinkVerse.AI datasource
        </p>
      </div>
    </div>
  );
};

export default ChatbotPopup;
